// Step 2: Budget Allocation & Ad Vendor - Alpine.js Component
// Handles dynamic budget allocation across tactics and vendors with AI suggestions

document.addEventListener('alpine:init', () => {
  Alpine.data('campaigns_step2_x_data', () => ({
    tactics: [],
    tacticDisplayNames: {}, // Tactic display names from backend
    tacticVendors: {}, // Track vendors for each tactic
    campaignBudget: 0, // Total campaign budget
    remainingBudget: 0, // Remaining budget
    totalAllocated: 0, // Total allocated budget
    totalDownloads: 0, // Total estimated downloads
    totalImpressions: 0, // Total estimated impressions
    estimationsUrl: '', // URL for estimations endpoint
    aiSuggestionsUrl: '', // URL for AI suggestions endpoint
    saveDraftUrl: '', // URL for save draft endpoint
    aiResults: null, // AI suggestions data
    aiSuggestionsAppliedFlag: false, // Flag indicating if AI suggestions were actually applied
    isProcessing: false, // Processing state for AI suggestions
    aiSuggestionsIgnored: false, // Track if AI suggestions were ignored
    aiSuggestionsApplied: false, // Track if AI suggestions were applied
    previousTacticVendors: null, // Store previous vendor selections before applying AI
    rationaleComplete: false, // Track if rationale typewriter effect is complete
    allTypewriterComplete: false, // Track if all typewriter effects are complete
    typewriterTexts: {}, // Store typewriter text states
    typewriterIntervals: {}, // Store typewriter intervals for cleanup
    
    // Computed property to check if Next button should be enabled
    get canProceedToNext() {
      // Don't check until data is loaded
      if (!this.campaignBudget || !this.tactics || Object.keys(this.tacticVendors).length === 0) {
        return false;
      }
      
      const tacticsData = this.getTacticsData();
      
      // Check if at least one tactic has budget allocation
      if (!tacticsData || tacticsData.length === 0) {
        return false;
      }
      
      const hasBudgetAllocation = tacticsData.some(tactic => 
        tactic.budget_allocation && parseFloat(tactic.budget_allocation) > 0
      );
      
      if (!hasBudgetAllocation) {
        return false;
      }
      
      // Check if total allocation matches campaign budget
      const totalAllocated = tacticsData.reduce((sum, tactic) => {
        return sum + (parseFloat(tactic.budget_allocation) || 0);
      }, 0);
      
      const budgetMatch = Math.abs(totalAllocated - this.campaignBudget) <= 0.01;
      
      return budgetMatch;
    },
    
    init() {
      this.loadCampaignBudget();
      this.loadTacticsData();
      this.loadTacticsDisplayNames();
      this.loadEstimationsUrl();
      this.loadAISuggestionsUrl();
      this.loadSaveDraftUrl();
      this.loadExistingAISuggestions();
      this.loadAISuggestionsAppliedFlag();
      
      // Load completed step 2 data or draft data after tactics data is loaded
      this.$nextTick(() => {
        this.loadCompletedStep2Data();
        this.updateBudgetDisplay(); // Update budget display after data is loaded
        this.calculateEstimates();
      });
    },
    
    loadCampaignBudget() {
      const template = this.$refs.campaign_budget_template;
      if (template && template.dataset.campaignBudget) {
        try {
          this.campaignBudget = parseFloat(template.dataset.campaignBudget) || 0;
          this.remainingBudget = this.campaignBudget;
        } catch (error) {
          console.error('Error parsing campaign budget:', error);
        }
      }
    },
    
    loadTacticsData() {
      const template = this.$refs.tactics_template;
      if (template && template.dataset.tactics) {
        try {
          this.tactics = JSON.parse(template.dataset.tactics);
          this.initializeTacticVendors();
          this.renderTactics();
        } catch (error) {
          console.error('Error parsing tactics data:', error);
        }
      }
    },

    loadTacticsDisplayNames() {
      const template = this.$refs.tactics_display_names_template;
      if (template && template.dataset.tacticsDisplayNames) {
        try {
          this.tacticDisplayNames = JSON.parse(template.dataset.tacticsDisplayNames);
        } catch (error) {
          console.error('Error parsing tactic display names:', error);
        }
      }
    },

    loadEstimationsUrl() {
      const template = this.$refs.estimations_url_template;
      if (template && template.dataset.estimationsUrl) {
        this.estimationsUrl = template.dataset.estimationsUrl;
      }
    },

    loadAISuggestionsUrl() {
      const template = this.$refs.ai_suggestions_url_template;
      if (template && template.dataset.aiSuggestionsUrl) {
        this.aiSuggestionsUrl = template.dataset.aiSuggestionsUrl;
      }
    },

    loadSaveDraftUrl() {
      const template = this.$refs.save_draft_url_template;
      if (template && template.dataset.saveDraftUrl) {
        this.saveDraftUrl = template.dataset.saveDraftUrl;
      }
    },

    loadAISuggestionsAppliedFlag() {
      const template = this.$refs.ai_suggestions_applied_template;
      if (template && template.dataset.aiSuggestionsApplied) {
        this.aiSuggestionsAppliedFlag = template.dataset.aiSuggestionsApplied === 'true';
      }
    },


    loadExistingAISuggestions() {
      const template = this.$refs.existing_ai_suggestions_template;
      if (template && template.dataset.existingAiSuggestions) {
        try {
          const existingSuggestions = JSON.parse(template.dataset.existingAiSuggestions);
          if (existingSuggestions && existingSuggestions.rationale && existingSuggestions.suggestions && Object.keys(existingSuggestions).length > 0) {
            this.aiResults = existingSuggestions;
            // Only mark as applied if there's a flag indicating they were actually applied
            // Check if the suggestions were actually applied (not just generated)
            this.aiSuggestionsApplied = this.checkIfAISuggestionsWereApplied();
            
            // If AI suggestions were applied, we need to apply them to restore the tactics data
            if (this.aiSuggestionsApplied) {
              this.$nextTick(() => {
                this.applyAISuggestionsFromDraft();
              });
            } else {
              // Start typewriter effect for existing suggestions that weren't applied
              this.$nextTick(() => {
                this.startTypewriterEffect();
              });
            }
          } else {
            this.aiResults = null;
          }
        } catch (error) {
          console.error('Error parsing existing AI suggestions:', error);
          this.aiResults = null;
        }
      } else {
        this.aiResults = null;
      }
    },

    loadCompletedStep2Data() {
      // Data loading priority:
      // 1. Completed step 2 data from campaign_tactics table (highest priority)
      // 2. Draft data from campaign_metadata (fallback)
      // 3. Empty form if neither exists
      
      // First try to load completed step 2 data from campaign_tactics table
      const completedTemplate = this.$refs.completed_step_2_template;
      
      if (completedTemplate && completedTemplate.dataset['completedStep-2']) {
        try {
          const completedData = JSON.parse(completedTemplate.dataset['completedStep-2']);
          if (completedData && completedData.length > 0) {
            this.restoreDraftData(completedData);
            return; // Don't load draft data if we have completed data
          }
        } catch (error) {
          console.error('Error parsing completed step 2 data:', error);
        }
      }
      
      // Fall back to loading draft data if no completed data exists
      this.loadSavedDraftData();
    },

    loadSavedDraftData() {
      const template = this.$refs.saved_draft_template;
      if (template && template.dataset.savedDraft) {
        try {
          const savedDraft = JSON.parse(template.dataset.savedDraft);
          if (savedDraft && savedDraft.tactics && savedDraft.tactics.length > 0) {
            this.restoreDraftData(savedDraft.tactics);
          }
        } catch (error) {
          console.error('Error parsing saved draft data:', error);
        }
      }
    },

    restoreDraftData(tacticsData) {
      // Validate input data
      if (!Array.isArray(tacticsData) || tacticsData.length === 0) {
        this.initializeTacticVendors();
        return;
      }
      
      // Clear existing data
      this.tacticVendors = {};
      
      // Restore tactics and vendors from saved data
      tacticsData.forEach(tacticData => {
        // Validate individual tactic data
        if (!tacticData || !tacticData.tactic || !tacticData.ad_vendor) {
          console.warn('Skipping invalid tactic data:', tacticData);
          return;
        }
        
        const tacticId = tacticData.tactic;
        if (!this.tacticVendors[tacticId]) {
          this.tacticVendors[tacticId] = [];
        }
        
        // Add vendor with saved data
        this.tacticVendors[tacticId].push({
          vendor: tacticData.ad_vendor,
          budget: tacticData.budget_allocation || 0
        });
      });
      
      // Ensure all tactics have at least one vendor entry
      this.tactics.forEach(tactic => {
        if (!this.tacticVendors[tactic.id]) {
          this.tacticVendors[tactic.id] = [];
        }
        
        // If no vendors for this tactic, add an empty one
        if (this.tacticVendors[tactic.id].length === 0) {
          this.tacticVendors[tactic.id].push({
            vendor: '',
            budget: 0
          });
        }
      });
      
      // Re-render and update calculations
      this.renderTactics();
      this.updateBudgetDisplay();
      this.calculateEstimates();
    },
    
    initializeTacticVendors() {
      this.tactics.forEach(tactic => {
        this.tacticVendors[tactic.id] = [];
        // Add initial vendor entry for each tactic
        this.addVendor(tactic.id);
      });
      
      // Ensure all vendor objects have the required properties
      this.ensureVendorProperties();
    },
    
    ensureVendorProperties() {
      Object.keys(this.tacticVendors).forEach(tacticId => {
        if (this.tacticVendors[tacticId] && Array.isArray(this.tacticVendors[tacticId])) {
          this.tacticVendors[tacticId].forEach(vendor => {
            if (vendor) {
              vendor.vendor = vendor.vendor || '';
              vendor.budget = vendor.budget || 0;
            }
          });
        }
      });
    },
    
    addVendor(tacticId, event) {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      
      const tactic = this.tactics.find(t => t.id === tacticId);
      if (!tactic) return;
      
      // Check if there are any available vendors to add
      const availableVendors = this.getAvailableVendorsForTactic(tacticId, -1);
      if (availableVendors.length === 0) {
        alert(`All available vendors for ${tactic.name} are already in use.`);
        return;
      }
      
      this.tacticVendors[tacticId].push({
        id: Date.now() + Math.random(), // Unique ID
        vendor: '',
        budget: 0
      });
      
      this.renderTactics();
      this.updateBudgetDisplay();
      // Calculate new estimates when vendor is added
      this.calculateEstimates();
    },
    
    removeVendor(tacticId, vendorIndex) {
      if (this.tacticVendors[tacticId] && this.tacticVendors[tacticId][vendorIndex]) {
        this.tacticVendors[tacticId].splice(vendorIndex, 1);
        
        // Ensure there's always at least one vendor entry for each tactic
        if (this.tacticVendors[tacticId].length === 0) {
          this.tacticVendors[tacticId].push({
            vendor: '',
            budget: 0
          });
        }
        
        this.renderTactics();
        this.updateBudgetDisplay();
        // Calculate new estimates when vendor is removed
        this.calculateEstimates();
      }
    },
    
    updateVendor(tacticId, vendorIndex, property, value) {
      if (this.tacticVendors[tacticId] && this.tacticVendors[tacticId][vendorIndex]) {
        // If updating vendor selection and trying to deselect
        if (property === 'vendor' && !value && this.tacticVendors[tacticId][vendorIndex].vendor) {
          const currentVendor = this.tacticVendors[tacticId][vendorIndex].vendor;
          const currentBudget = this.tacticVendors[tacticId][vendorIndex].budget;
          
          if (currentBudget > 0) {
            const confirmed = confirm(`Deselecting "${currentVendor}" will remove the budget amount ($${currentBudget.toLocaleString()}) and disable the budget input. Are you sure you want to continue?`);
            if (!confirmed) {
              // User cancelled, revert the selection by not updating anything
              return;
            }
          }
        }
        
        this.tacticVendors[tacticId][vendorIndex][property] = value;
        
        // If updating vendor selection
        if (property === 'vendor') {
          if (value) {
            // Vendor selected - validate budget against minimum spend
            this.validateBudgetAgainstVendor(tacticId, vendorIndex, value);
          } else {
            // Vendor deselected - clear budget
            this.tacticVendors[tacticId][vendorIndex].budget = 0;
          }
        }
        
        // Re-render to show the updated state
        this.renderTactics();
        
        // Update budget display when vendor or budget changes
        if (property === 'vendor' || property === 'budget') {
          this.updateBudgetDisplay();
          // Calculate new estimates when vendor or budget changes
          this.calculateEstimates();
        }
      }
    },
    
    validateBudgetAgainstVendor(tacticId, vendorIndex, vendorName) {
      const tactic = this.tactics.find(t => t.id === tacticId);
      if (!tactic) return;
      
      const vendor = tactic.ad_vendors.find(v => v.name === vendorName);
      if (!vendor || !vendor.minimum_spend) return;
      
      const currentBudget = this.tacticVendors[tacticId][vendorIndex].budget;
      if (currentBudget < vendor.minimum_spend) {
        // Check if setting to minimum spend would exceed campaign budget
        const currentTotalAllocated = this.calculateTotalAllocated();
        const currentVendorBudget = currentBudget || 0;
        const newTotalAllocated = currentTotalAllocated - currentVendorBudget + vendor.minimum_spend;
        
        if (newTotalAllocated > this.campaignBudget) {
          // Can't set to minimum spend without exceeding budget
          this.showBudgetExceededError(vendor.minimum_spend, this.campaignBudget, currentTotalAllocated);
          // Clear the vendor selection since we can't meet minimum spend
          this.tacticVendors[tacticId][vendorIndex].vendor = '';
          this.renderTactics();
          return;
        }
        
        // Show warning and update budget to minimum
        this.tacticVendors[tacticId][vendorIndex].budget = vendor.minimum_spend;
        this.showMinimumSpendWarning(vendor.display_name, vendor.minimum_spend);
        this.renderTactics(); // Re-render to show updated budget
      }
    },
    
    showMinimumSpendWarning(vendorName, minimumSpend) {
      const message = `${vendorName} requires a minimum spend of $${minimumSpend.toLocaleString()}. Budget has been updated automatically.`;
      // You can replace this with a more sophisticated notification system
      // alert(message);
    },
    
    getVendorMinimumSpend(tacticId, vendorName) {
      const tactic = this.tactics.find(t => t.id === tacticId);
      if (!tactic) return 0;
      
      const vendor = tactic.ad_vendors.find(v => v.name === vendorName);
      return vendor ? vendor.minimum_spend : 0;
    },
    
    validateBudgetInput(tacticId, vendorIndex, value) {
      // Ensure value is a clean integer
      const cleanValue = value.toString().replace(/[^0-9]/g, '');
      const budget = parseInt(cleanValue) || 0;
      const vendor = this.tacticVendors[tacticId][vendorIndex];
      
      if (!vendor || !vendor.vendor) {
        // Don't allow budget input if no vendor selected
        return;
      }
      
      const minimumSpend = this.getVendorMinimumSpend(tacticId, vendor.vendor);
      
      // Check if budget exceeds minimum spend
      if (budget < minimumSpend) {
        // Show error and revert to previous valid value
        const previousBudget = vendor.budget || 0;
        this.tacticVendors[tacticId][vendorIndex].budget = previousBudget;
        this.showBudgetError(vendor.vendor, minimumSpend);
        this.renderTactics(); // Re-render to show reverted value
        return;
      }
      
      // Check if budget would exceed campaign budget
      const currentTotalAllocated = this.calculateTotalAllocated();
      const currentVendorBudget = vendor.budget || 0;
      const newTotalAllocated = currentTotalAllocated - currentVendorBudget + budget;
      
      if (newTotalAllocated > this.campaignBudget) {
        // Show error and revert to previous valid value
        const previousBudget = vendor.budget || 0;
        this.tacticVendors[tacticId][vendorIndex].budget = previousBudget;
        this.showBudgetExceededError(budget, this.campaignBudget, currentTotalAllocated);
        this.renderTactics(); // Re-render to show reverted value
        return;
      }
      
      // Valid budget, update normally
      this.updateVendor(tacticId, vendorIndex, 'budget', budget);
      this.updateBudgetDisplay();
      // Calculate new estimates when budget changes
      this.calculateEstimates();
    },
    
    showBudgetError(vendorName, minimumSpend) {
      const message = `${vendorName} requires a minimum spend of $${minimumSpend.toLocaleString()}. Please enter a lower amount.`;
      alert(message);
    },
    
    showBudgetExceededError(attemptedBudget, campaignBudget, currentAllocated) {
      const remainingBudget = campaignBudget - currentAllocated;
      const message = `Budget allocation of $${attemptedBudget.toLocaleString()} would exceed your campaign budget of $${campaignBudget.toLocaleString()}. You have $${remainingBudget.toLocaleString()} remaining.`;
      alert(message);
    },
    
    getAvailableVendorsForTactic(tacticId, currentVendorIndex) {
      const tactic = this.tactics.find(t => t.id === tacticId);
      if (!tactic) return [];
      
      const currentVendors = this.tacticVendors[tacticId] || [];
      const selectedVendors = currentVendors
        .map((v, index) => ({ vendor: v.vendor, index }))
        .filter(v => v.vendor && v.index !== currentVendorIndex)
        .map(v => v.vendor);
      
      // Return all vendors except the ones already selected in other rows
      return tactic.ad_vendors.filter(vendor => !selectedVendors.includes(vendor.name));
    },
    
    getMaxVendorsForTactic(tacticId) {
      // Get the tactic data from the loaded tactics
      const tactic = this.tactics.find(t => t.id === tacticId);
      if (tactic && tactic.ad_vendors) {
        return tactic.ad_vendors.length;
      }
      return 1; // Fallback to 1 if tactic not found
    },
    
    renderTactics() {
      const container = document.getElementById('tactics-container');
      if (!container) return;
      
      container.innerHTML = '';
      
      // Get all unique tactic IDs from both this.tactics and tacticVendors
      const allTacticIds = new Set([
        ...this.tactics.map(t => t.id),
        ...Object.keys(this.tacticVendors)
      ]);
      
      allTacticIds.forEach(tacticId => {
        // Try to find the tactic by exact ID first
        let tactic = this.tactics.find(t => t.id === tacticId);
        
        if (!tactic) {
          // Create a fallback tactic object for AI suggestions
          tactic = { 
            id: tacticId, 
            name: this.getTacticDisplayName(tacticId),
            minimum_spend: 0,
            ad_vendors: []
          };
        }
        
        const vendors = this.tacticVendors[tacticId] || [];
        
        vendors.forEach((vendor, vendorIndex) => {
          const tacticRow = this.createTacticRow(tactic, vendor, vendorIndex);
          if (tacticRow) {
            container.appendChild(tacticRow);
          }
        });
      });
      
      // Use Alpine.js $nextTick to ensure DOM is updated
      if (this.$nextTick) {
        this.$nextTick(() => {
          this.ensureVendorProperties();
        });
        
        // Update budget display after initial render
        this.updateBudgetDisplay();
        // Calculate estimates after initial render
        this.calculateEstimates();
      }
    },
    
    createTacticRow(tactic, vendor, vendorIndex) {
      // Safety check to ensure vendor exists
      if (!vendor || !tactic) {
        console.error('Invalid vendor or tactic data:', { vendor, tactic });
        return null;
      }
      
      // Ensure vendor object has all required properties
      vendor.vendor = vendor.vendor || '';
      vendor.budget = vendor.budget || 0;
      
      const row = document.createElement('div');
      row.className = 'px-6 py-4';
      
      const isFirstVendor = vendorIndex === 0;
      const canRemove = this.tacticVendors[tactic.id] && this.tacticVendors[tactic.id].length > 1;
      
      row.innerHTML = `
        <div class="grid grid-cols-12 gap-4 items-center">
          <div class="col-span-5">
            ${isFirstVendor ? `
              <div class="flex items-center space-x-2">
                <a href="#" class="font-semibold text-black hover:text-gray-800">${tactic.name}</a>
                <button 
                  type="button"
                  class="text-sm text-blue-600 hover:text-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded"
                  onclick="openCampaignModal('${tactic.id}', '${tactic.name}', '${this.getTacticDescription(tactic.id)}')"
                  data-tactic-type="${tactic.id}">
                  See example
                </button>
              </div>
              <div class="text-sm text-gray-600">Minimum spend: $${tactic.minimum_spend.toLocaleString()}</div>
              ${this.tacticVendors[tactic.id].length < tactic.ad_vendors.length ? `<button type="button" class="text-quill-active hover:text-quill-selected text-sm" @click="addVendor('${tactic.id}', $event)">+ Vendor</button>` : ''}
            ` : ''}
          </div>
          <div class="col-span-4 relative">
            ${(!isFirstVendor || (isFirstVendor && vendor.vendor)) ? `
              <button style="position: absolute; left: -2rem; top: 50%; transform: translateY(-50%); width: 1.5rem; height: 1.5rem; border: 2px solid #ef4444; color: #ef4444; border-radius: 50%; display: flex; align-items: center; justify-content: center; background-color: white; z-index: 10;" class="hover:bg-red-50" @click="removeVendor('${tactic.id}', ${vendorIndex})">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M5 12h14"/>
                </svg>
              </button>
            ` : ''}
            <select class="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-quill-active focus:border-quill-active text-sm" @change="updateVendor('${tactic.id}', ${vendorIndex}, 'vendor', $event.target.value)">
              <option value="">Select a vendor</option>
              ${this.getAvailableVendorsForTactic(tactic.id, vendorIndex).map(v => `<option value="${v.name}" ${vendor.vendor === v.name ? 'selected' : ''}>${v.display_name}</option>`).join('')}
            </select>
          </div>
          <div class="col-span-3">
            <div class="relative">
              <input type="number" 
              value="${vendor.budget || 0}" 
              min="${this.getVendorMinimumSpend(tactic.id, vendor.vendor)}" step="1" ${!vendor.vendor ? 'disabled' : ''} class="w-full px-3 py-2  border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-quill-active focus:border-quill-active text-sm ${!vendor.vendor ? 'bg-gray-100 cursor-not-allowed' : ''}" placeholder="${vendor.vendor ? `Min: $${this.getVendorMinimumSpend(tactic.id, vendor.vendor).toLocaleString()}` : 'Select vendor first'}" @blur="validateBudgetInput('${tactic.id}', ${vendorIndex}, $event.target.value)" @input="preventDecimals($event)">

            </div>
          </div>
        </div>
      `;
      
      return row;
    },
    


    getTacticDisplayName(tacticId) {
      // Get tactic display name from backend data
      return this.tacticDisplayNames[tacticId] || tacticId;
    },

    getTacticsData() {
      const data = [];
      console.log('Getting tactics data from tacticVendors:', this.tacticVendors);
      Object.keys(this.tacticVendors).forEach(tacticId => {
        if (this.tacticVendors[tacticId] && Array.isArray(this.tacticVendors[tacticId])) {
          this.tacticVendors[tacticId].forEach(vendor => {
            console.log(`Processing vendor for tactic ${tacticId}:`, vendor);
            if (vendor && vendor.vendor && parseFloat(vendor.budget) > 0) {
              data.push({
                tactic: tacticId,
                ad_vendor: vendor.vendor,
                budget_allocation: vendor.budget
              });
            }
          });
        }
      });
      console.log('Final tactics data:', data);
      return data;
    },
    
    calculateTotalAllocated() {
      let total = 0;
      
      Object.keys(this.tacticVendors).forEach(tacticId => {
        if (this.tacticVendors[tacticId] && Array.isArray(this.tacticVendors[tacticId])) {
          this.tacticVendors[tacticId].forEach(vendor => {
            if (vendor && vendor.vendor && parseFloat(vendor.budget) > 0) {
              const budgetValue = parseFloat(vendor.budget) || 0;
              total += budgetValue;
            }
          });
        }
      });
      
      this.totalAllocated = total;
      return total;
    },
    
    updateBudgetDisplay() {
      this.calculateTotalAllocated();
      this.remainingBudget = this.campaignBudget - this.totalAllocated;
    },
    
    get remainingBudgetPercentage() {
      if (this.campaignBudget === 0) return 0;
      return Math.round((this.remainingBudget / this.campaignBudget) * 100);
    },
    
    get totalAllocatedPercentage() {
      if (this.campaignBudget === 0) return 0;
      return Math.round((this.totalAllocated / this.campaignBudget) * 100);
    },
    
    isBudgetExceeded() {
      return this.remainingBudget < 0;
    },
    
    getExceededAmount() {
      return this.isBudgetExceeded() ? Math.abs(this.remainingBudget) : 0;
    },
    
    preventDecimals(event) {
      const input = event.target;
      const value = input.value;
      
      // Remove any decimal points and everything after them
      if (value.includes('.')) {
        input.value = value.split('.')[0];
      }
      
      // Remove any non-numeric characters except for the minus sign
      input.value = input.value.replace(/[^0-9]/g, '');
    },

    // Estimation calculation methods
    async calculateEstimates() {
      const allocations = this.getTacticsData();
      console.log('Calculating estimates with allocations:', allocations);
      let totalDownloads = 0;
      let totalImpressions = 0;

      for (const allocation of allocations) {
        console.log(`Processing allocation:`, allocation);
        if (allocation.budget_allocation > 0) {
          console.log(`Budget allocation ${allocation.budget_allocation} > 0, fetching estimates...`);
          try {
            const estimates = await this.fetchEstimates(
              allocation.tactic,
              allocation.ad_vendor,
              allocation.budget_allocation
            );
            
            console.log(`Received estimates:`, estimates);
            if (estimates.downloads && estimates.downloads !== '—') {
              const downloads = parseInt(estimates.downloads) || 0;
              totalDownloads += downloads;
              console.log(`Added ${downloads} downloads, total now: ${totalDownloads}`);
            }
            
            if (estimates.impressions && estimates.impressions !== '—') {
              const impressions = parseInt(estimates.impressions) || 0;
              totalImpressions += impressions;
              console.log(`Added ${impressions} impressions, total now: ${totalImpressions}`);
            }
          } catch (error) {
            console.error('Error fetching estimates:', error);
          }
        } else {
          console.log(`Budget allocation ${allocation.budget_allocation} is not > 0, skipping`);
        }
      }

      // Update the display values
      console.log(`Final totals - Downloads: ${totalDownloads}, Impressions: ${totalImpressions}`);
      this.totalDownloads = totalDownloads > 0 ? totalDownloads.toLocaleString() : '—';
      this.totalDownloadsRange = '';
      this.totalImpressions = totalImpressions > 0 ? totalImpressions.toLocaleString() : '—';
      console.log(`Display values set - Downloads: ${this.totalDownloads}, Impressions: ${this.totalImpressions}`);
    },

    async fetchEstimates(tactic, vendor, budget) {
      try {
        console.log(`Fetching estimates for tactic: ${tactic}, vendor: ${vendor}, budget: ${budget}`);
        const response = await fetch(this.estimationsUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': this.getCsrfToken()
          },
          body: JSON.stringify({
            tactic: tactic,
            vendor: vendor,
            budget: budget,
            campaign_id: this.getCampaignId()
          })
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        console.log(`Estimates result for ${tactic}:`, result);
        return result;
      } catch (error) {
        console.error('Error fetching estimates:', error);
        return { downloads: '—', impressions: '—' };
      }
    },


    getCampaignId() {
      // Extract campaign ID from the current URL
      const urlParts = window.location.pathname.split('/');
      // URL structure: /shows/:show_id/campaigns/:campaign_id/step_2
      // So campaign_id is at index 4 (after empty string, 'shows', show_id, 'campaigns')
      return urlParts[4];
    },

    getShowId() {
      // Extract show ID from the current URL
      const urlParts = window.location.pathname.split('/');
      // URL structure: /shows/:show_id/campaigns/:campaign_id/step_2
      // So show_id is at index 2 (after empty string, 'shows')
      return urlParts[2];
    },

    getCsrfToken() {
      return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
    },

    getTacticDescription(tacticId) {
      const tactic = this.tactics.find(t => t.id === tacticId);
      if (!tactic) return 'No description available.';
      
      switch (tactic.id) {
        case 'display_banner_ads':
          return 'These ads are displayed on trusted digital publishers networks such as Forbes, Fortune, and more.<br><br>Best for: <strong>Brand awareness</strong> and <strong>gaining downloads</strong>.';
        case 'linkedin_ads':
          return 'A premium podcast download campaign using LinkedIn Ads, allowing hyper-specific audience targeting by job title, company name, industry, and seniority. Delivers about 300 impressions per download, maximizing B2B brand exposure.<br><br>Best for: <strong>Brand awareness</strong> and <strong>specific company targeting</strong>.';
        case 'in_app_ads':
          return 'Promote your promote within podcast listening apps or software such as PlayerFM and Headliner.<br><br>Best for: <strong>Broad brand awareness</strong>.';
        case 'youtube_ads':
          return 'Grow your YouTube channel with real, engaged viewers and subscribers through fully managed, custom-targeted campaigns. YouTube Ads optimize for sustained organic growth, avoiding bots and low-quality traffic while aligning with the YouTube algorithm.<br><br>Best for: <strong>Brand awareness</strong> and <strong>video podcasts</strong>.';
        default:
          return `Example content for ${tactic.name.toLowerCase()}.`;
      }
    },

    // Typewriter effect methods
    startTypewriterEffect() {
      if (!this.aiResults) return;
      
      // Reset state
      this.rationaleComplete = false;
      this.allTypewriterComplete = false;
      this.typewriterTexts = {};
      this.clearTypewriterIntervals();
      
      // Start with rationale
      this.startRationaleTypewriter();
    },

    startRationaleTypewriter() {
      if (!this.aiResults.rationale || !Array.isArray(this.aiResults.rationale)) return;
      
      let currentIndex = 0;
      const rationale = this.aiResults.rationale;
      
      // Initialize all rationale text keys as empty
      rationale.forEach((_, index) => {
        this.typewriterTexts[`rationale_${index}`] = '';
      });
      
      const typeNextRationale = () => {
        if (currentIndex >= rationale.length) {
          // All rationale items are complete, start suggestions
          this.rationaleComplete = true;
          if (this.aiResults.suggestions && this.aiResults.suggestions.length > 0) {
            this.startSuggestionsTypewriter();
          } else {
            // No suggestions, mark all typewriter as complete
            this.allTypewriterComplete = true;
          }
          return;
        }
        
        const reason = rationale[currentIndex];
        this.typeText(`rationale_${currentIndex}`, reason, () => {
          currentIndex++;
          // Small delay before next rationale item
          setTimeout(typeNextRationale, 300);
        });
      };
      
      // Start typing rationale after a short delay
      setTimeout(typeNextRationale, 300);
    },

    startSuggestionsTypewriter() {
      if (!this.aiResults.suggestions || !Array.isArray(this.aiResults.suggestions)) return;
      
      let currentIndex = 0;
      const suggestions = this.aiResults.suggestions;
      
      // Initialize all suggestion text keys as empty
      suggestions.forEach((_, index) => {
        this.typewriterTexts[`suggestion_tactic_${index}`] = '';
        this.typewriterTexts[`suggestion_vendor_${index}`] = '';
        this.typewriterTexts[`suggestion_budget_${index}`] = '';
      });
      
      const typeNextSuggestion = () => {
        if (currentIndex >= suggestions.length) {
          // All suggestions are complete
          this.allTypewriterComplete = true;
          return;
        }
        
        const suggestion = suggestions[currentIndex];
        
        // Type tactic name
        this.typeText(`suggestion_tactic_${currentIndex}`, 
          suggestion.tactic.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()), 
          () => {
            // Type vendor name
            this.typeText(`suggestion_vendor_${currentIndex}`, 
              'Ad Vendor: ' + suggestion.ad_vendor.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
              () => {
                // Type budget
                this.typeText(`suggestion_budget_${currentIndex}`, 
                  '$' + suggestion.suggested_budget_allocation.toLocaleString() + ' (' + Math.round((suggestion.suggested_budget_allocation / this.campaignBudget) * 100) + '%)',
                  () => {
                    currentIndex++;
                    // Small delay before next suggestion
                    setTimeout(typeNextSuggestion, 400);
                  }
                );
              }
            );
          }
        );
      };
      
      // Start typing suggestions after rationale is complete
      setTimeout(typeNextSuggestion, 500);
    },

    typeText(key, fullText, onComplete) {
      let currentIndex = 0;
      const interval = setInterval(() => {
        if (currentIndex < fullText.length) {
          this.typewriterTexts[key] = fullText.substring(0, currentIndex + 1);
          currentIndex++;
        } else {
          clearInterval(interval);
          if (onComplete) onComplete();
        }
      }, 10); // Adjust speed here (lower = faster)
      
      // Store interval for cleanup
      this.typewriterIntervals[key] = interval;
    },

    getTypewriterText(type, index) {
      const key = `${type}_${index}`;
      return this.typewriterTexts[key] || '';
    },

    clearTypewriterIntervals() {
      Object.values(this.typewriterIntervals).forEach(interval => {
        clearInterval(interval);
      });
      this.typewriterIntervals = {};
    },

    async getAISuggestions() {
      if (!this.aiSuggestionsUrl) {
        console.error('AI suggestions URL not loaded');
        return;
      }

      this.isProcessing = true;
      
      const maxRetries = 2;
      let attempt = 0;
      
      while (attempt <= maxRetries) {
        try {
          const response = await fetch(this.aiSuggestionsUrl, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRF-Token': this.getCsrfToken()
            }
          });

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const data = await response.json();
          
          // Simulate processing time for better UX
          setTimeout(() => {
            this.isProcessing = false;
            this.aiResults = data;
            // Start typewriter effect after processing is complete
            this.startTypewriterEffect();
          }, 2000);
          
          // Success - break out of retry loop
          return;
          
        } catch (error) {
          attempt++;
          console.error(`Error fetching AI suggestions (attempt ${attempt}/${maxRetries + 1}):`, error);
          
          if (attempt > maxRetries) {
            // Final attempt failed
            console.error('All retry attempts failed for AI suggestions');
            this.isProcessing = false;
            // You could add error handling here, like showing a toast notification
            alert('CoHost AI is currently down. We apologies for the inconvenience. Please try again later.');
            return;
          }
          
          // Wait before retrying (exponential backoff)
          const delay = Math.pow(2, attempt) * 1000; // 2s, 4s delays
          console.log(`Retrying AI suggestions in ${delay}ms...`);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    },

    ignoreAISuggestions() {
      this.aiSuggestionsIgnored = true;
      this.aiResults = null;
      this.aiSuggestionsApplied = false;
      this.previousTacticVendors = null;
      this.rationaleComplete = false;
      this.allTypewriterComplete = false;
      this.clearTypewriterIntervals();
      
      // Clear the applied flag from campaign metadata
      this.clearAISuggestionsAppliedFlag();
      
      // The card will automatically hide due to x-show="aiResults"
    },

    applyAISuggestions() {
      if (!this.aiResults || !this.aiResults.suggestions || !this.aiResults.rationale) {
        console.error('No valid AI suggestions to apply:', this.aiResults);
        return;
      }

      // Store current selections before applying AI suggestions
      this.storeCurrentSelections();

      // Instead of clearing all vendors, we'll update existing ones or add new ones
      // First, let's track which tactics are being updated by AI suggestions
      const aiUpdatedTactics = new Set();
      
      // Process AI suggestions and group them by tactic
      const suggestionsByTactic = {};
      this.aiResults.suggestions.forEach(suggestion => {
        const tacticId = suggestion.tactic;
        aiUpdatedTactics.add(tacticId);
        
        if (!suggestionsByTactic[tacticId]) {
          suggestionsByTactic[tacticId] = [];
        }
        
        suggestionsByTactic[tacticId].push({
          id: Date.now() + Math.random(),
          vendor: suggestion.ad_vendor,
          budget: parseFloat(suggestion.suggested_budget_allocation) || 0
        });
      });

      // Apply AI suggestions to tacticVendors
      Object.keys(suggestionsByTactic).forEach(tacticId => {
        this.tacticVendors[tacticId] = suggestionsByTactic[tacticId];
        // Updated tactic with AI suggestions
      });

      // For tactics NOT covered by AI suggestions, keep their existing vendors
      // but clear their budgets so they don't affect the total allocation
      Object.keys(this.tacticVendors).forEach(tacticId => {
        if (!aiUpdatedTactics.has(tacticId)) {
          // Keep existing vendors but clear budgets
          this.tacticVendors[tacticId].forEach(vendor => {
            vendor.budget = 0;
          });
        }
      });



      // Ensure all vendor objects have the required properties
      this.ensureVendorProperties();

      // Re-render and update calculations
      this.renderTactics();
      this.updateBudgetDisplay();
      this.calculateEstimates();
      
      // Mark AI suggestions as applied and show restore button
      this.aiSuggestionsApplied = true;
      
      // Store the applied flag in campaign metadata for persistence
      this.saveAISuggestionsAppliedFlag();
    },

    // Apply AI suggestions from draft data (without saving to backend)
    applyAISuggestionsFromDraft() {
      if (!this.aiResults || !this.aiResults.suggestions || !this.aiResults.rationale) {
        console.error('No valid AI suggestions to apply from draft:', this.aiResults);
        return;
      }

      // Process AI suggestions and group them by tactic
      const suggestionsByTactic = {};
      this.aiResults.suggestions.forEach(suggestion => {
        const tacticId = suggestion.tactic;
        
        if (!suggestionsByTactic[tacticId]) {
          suggestionsByTactic[tacticId] = [];
        }
        
        suggestionsByTactic[tacticId].push({
          id: Date.now() + Math.random(),
          vendor: suggestion.ad_vendor,
          budget: parseFloat(suggestion.suggested_budget_allocation) || 0
        });
      });

      // Apply AI suggestions to tacticVendors
      Object.keys(suggestionsByTactic).forEach(tacticId => {
        this.tacticVendors[tacticId] = suggestionsByTactic[tacticId];
      });

      // Ensure all vendor objects have the required properties
      this.ensureVendorProperties();

      // Re-render and update calculations
      this.renderTactics();
      this.updateBudgetDisplay();
      this.calculateEstimates();
      
      // Start typewriter effect to show the AI suggestions
      this.startTypewriterEffect();
    },

    // Store current selections before applying AI suggestions
    storeCurrentSelections() {
      this.previousTacticVendors = JSON.parse(JSON.stringify(this.tacticVendors));
    },

    // Restore previous vendor selections
    restorePreviousSelections() {
      if (!this.previousTacticVendors) {
        return;
      }
      
      // Restore the previous vendor selections
      this.tacticVendors = JSON.parse(JSON.stringify(this.previousTacticVendors));
      
      // Hide the restore button
      this.aiSuggestionsApplied = false;
      
      // Clear the applied flag from campaign metadata
      this.clearAISuggestionsAppliedFlag();
      
      // Show notification
      this.showUndoNotification();
      
      // Re-render and update calculations
      this.renderTactics();
      this.updateBudgetDisplay();
      this.calculateEstimates();
    },

    // Show notification for AI suggestions undo
    showUndoNotification() {
      // Store the notification in sessionStorage for the Alpine toast component
      const notification = ['notice', 'AI Suggestions have been undone in the tactics table'];
      sessionStorage.setItem('flash_message', JSON.stringify(notification));
      
      // Trigger a custom event to show the notification immediately
      window.dispatchEvent(new CustomEvent('show-toast-notification', {
        detail: {
          type: 'notice',
          message: 'AI Suggestions have been undone in the tactics table'
        }
      }));
    },

    // Save AI suggestions applied flag to campaign metadata
    async saveAISuggestionsAppliedFlag() {
      try {
        const response = await fetch(this.saveDraftUrl, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': this.getCsrfToken()
          },
          body: JSON.stringify({
            campaign: {
              ai_suggestions_applied: true
            }
          })
        });

        if (!response.ok) {
          console.error('Failed to save AI suggestions applied flag');
        }
      } catch (error) {
        console.error('Error saving AI suggestions applied flag:', error);
      }
    },

    // Clear AI suggestions applied flag from campaign metadata
    async clearAISuggestionsAppliedFlag() {
      try {
        const response = await fetch(this.saveDraftUrl, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': this.getCsrfToken()
          },
          body: JSON.stringify({
            campaign: {
              ai_suggestions_applied: false
            }
          })
        });

        if (!response.ok) {
          console.error('Failed to clear AI suggestions applied flag');
        }
      } catch (error) {
        console.error('Error clearing AI suggestions applied flag:', error);
      }
    },

    // Check if AI suggestions were actually applied (not just generated)
    checkIfAISuggestionsWereApplied() {
      // Check if there's a flag in the campaign metadata indicating AI suggestions were applied
      const template = this.$refs.ai_suggestions_applied_template;
      if (template && template.dataset.aiSuggestionsApplied) {
        return template.dataset.aiSuggestionsApplied === 'true';
      }
      return false;
    },

    // Save draft functionality
    save_draft() {
      const tacticsData = this.getTacticsData();
      
      // Create a form to submit the data
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = this.saveDraftUrl;
      
      // Add CSRF token
      const csrfToken = document.createElement('input');
      csrfToken.type = 'hidden';
      csrfToken.name = 'authenticity_token';
      csrfToken.value = this.getCsrfToken();
      form.appendChild(csrfToken);
      
      // Add Rails method override for PATCH
      const methodOverride = document.createElement('input');
      methodOverride.type = 'hidden';
      methodOverride.name = '_method';
      methodOverride.value = 'patch';
      form.appendChild(methodOverride);
      
      // Add tactics data
      if (tacticsData && tacticsData.length > 0) {
        const tacticsInput = document.createElement('input');
        tacticsInput.type = 'hidden';
        tacticsInput.name = 'campaign[tactics]';
        tacticsInput.value = JSON.stringify(tacticsData);
        form.appendChild(tacticsInput);
      }
      
      // Add AI suggestions data if available and applied
      if (this.aiResults && this.aiSuggestionsApplied) {
        const aiSuggestionsInput = document.createElement('input');
        aiSuggestionsInput.type = 'hidden';
        aiSuggestionsInput.name = 'campaign[ai_suggestions]';
        aiSuggestionsInput.value = JSON.stringify(this.aiResults);
        form.appendChild(aiSuggestionsInput);
        
        // Add AI suggestions applied flag
        const aiAppliedInput = document.createElement('input');
        aiAppliedInput.type = 'hidden';
        aiAppliedInput.name = 'campaign[ai_suggestions_applied]';
        aiAppliedInput.value = 'true';
        form.appendChild(aiAppliedInput);
      }
      
      // Submit the form (this will trigger a redirect with flash message)
      document.body.appendChild(form);
      form.submit();
    },

    // Validate step 2 before proceeding
    validateStep2() {
      const tacticsData = this.getTacticsData();
      
      // Check if at least one tactic has budget allocation
      if (!tacticsData || tacticsData.length === 0) {
        alert('Please add at least one tactic with budget allocation to continue.');
        return false;
      }
      
      // Check if any tactic has budget allocation
      const hasBudgetAllocation = tacticsData.some(tactic => 
        tactic.budget_allocation && parseFloat(tactic.budget_allocation) > 0
      );
      
      if (!hasBudgetAllocation) {
        alert('Please allocate budget to at least one tactic to continue.');
        return false;
      }
      
      // Validate minimum spend requirements for each tactic
      for (const tactic of tacticsData) {
        const minimumSpend = this.getVendorMinimumSpend(tactic.tactic, tactic.ad_vendor);
        const budgetAllocation = parseFloat(tactic.budget_allocation) || 0;
        
        if (budgetAllocation < minimumSpend) {
          alert(`${tactic.ad_vendor} for ${tactic.tactic} requires a minimum spend of $${minimumSpend.toLocaleString()}. Current allocation: $${budgetAllocation.toLocaleString()}.`);
          return false;
        }
      }
      
      // Check if total allocation matches campaign budget
      const totalAllocated = tacticsData.reduce((sum, tactic) => {
        return sum + (parseFloat(tactic.budget_allocation) || 0);
      }, 0);
      
      if (Math.abs(totalAllocated - this.campaignBudget) > 0.01) { // Allow for small rounding differences
        alert(`Total budget allocation ($${totalAllocated.toLocaleString()}) must match campaign budget ($${this.campaignBudget.toLocaleString()}) to continue.`);
        return false;
      }
      
      return true;
    },

    // Next step functionality
    next_step() {
      // Validate before proceeding
      if (!this.validateStep2()) {
        return;
      }
      
      const tacticsData = this.getTacticsData();
      console.log('Submitting tactics data:', tacticsData);
      
      // Get the form and set the tactics data
      const form = document.getElementById('step-2-form');
      const tacticsInput = document.getElementById('tactics-data-input');
      
      if (!form || !tacticsInput) {
        console.error('Form or tactics input not found');
        alert('Error: Form not found. Please refresh the page and try again.');
        return;
      }
      
      // Set the tactics data as JSON string
      tacticsInput.value = JSON.stringify(tacticsData);
      
      // Submit the form - server will handle redirect
      form.submit();
    }
  }));
});